import * as Sentry from "@sentry/react";
import {
  VITE_APPLICATION_VERSION,
  VITE_BUILD_NUMBER,
} from "Infrastructure/Utils/BuildUtils";
import { parseSentryUserFromJWT } from "Infrastructure/Utils/TrackingUtils";
import { useEffect } from "react";
import {
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from "react-router-dom";
import { isConnectSupersededError } from "Utils/ConnectSupersededError";

export const SENTRY_CONFIG = {
  IS_ENABLED: import.meta.env.VITE_SENTRY_IS_ENABLED === "true",
  DSN: import.meta.env.VITE_SENTRY_DSN as string,
  ORG: import.meta.env.VITE_SENTRY_ORG as string,
  PROJECT: import.meta.env.VITE_SENTRY_PROJECT as string,
};

export function initSentry() {
  //check if sentry was initialized
  if (SENTRY_CONFIG.IS_ENABLED && !Sentry.isInitialized()) {
    Sentry.init({
      dsn: SENTRY_CONFIG.DSN,
      integrations: [
        Sentry.reactRouterV7BrowserTracingIntegration({
          useEffect,
          useLocation,
          useNavigationType,
          createRoutesFromChildren,
          matchRoutes,
        }),
        Sentry.replayIntegration({
          maskAllText: false,
          maskAllInputs: false,
          networkDetailAllowUrls: [/znpay/, "localhost"],
          networkDetailDenyUrls: [/api\/health\/status/],
        }),
        Sentry.httpContextIntegration(),
        Sentry.dedupeIntegration(),
      ],
      environment: import.meta.env.VITE_VERSION as string,
      // Google's reCAPTCHA bundle throws inside its own load handler on some browsers; nothing in
      // the app is on that stack, so it only burns quota.
      denyUrls: [/\/recaptcha\/releases\//],
      tracePropagationTargets: [/ProjectTemplate/, "localhost"],
      replaysSessionSampleRate: 0,
      replaysOnErrorSampleRate: 1,
      tracesSampleRate: 1,
      normalizeDepth: 8,
      // Sentry's own hosted report dialog used to pop here on every exception. It is the widget
      // the in-app SupportButton replaced — leaving it would mean two report UIs, and it lost
      // its feature flag along with VITE_SENTRY_IS_FEEDBACK_ENABLED.
      beforeSend(event, hint) {
        if (hint.originalException === "Timeout") return null; //https://github.com/getsentry/sentry-javascript/issues/2514
        // A superseded Stream connect is a cancellation. reportApiError already drops it, but the
        // SDK's own reconnect/token refresh surfaces it as an unhandled rejection that bypasses it.
        if (isConnectSupersededError(hint.originalException)) {
          return null;
        }
        return event;
      },
      initialScope: (scope) => {
        const user = parseSentryUserFromJWT();
        if (user) {
          scope.setUser(user);
          scope.setTag("user.login", user.username);
        }

        scope.setTag("app.version", VITE_APPLICATION_VERSION);
        scope.setTag("app.build", VITE_BUILD_NUMBER);

        return scope;
      },
    });
  }
}
